import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  SparklesIcon,
  CheckCircleIcon,
  LightBulbIcon,
  UserGroupIcon,
  BoltIcon,
  ChatBubbleLeftIcon,
  RectangleStackIcon,
  WrenchIcon,
  FireIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline";

function Home() {
  const [recentJobs, setRecentJobs] = useState([]);

  useEffect(() => {
    fetchRecentJobs();
  }, []);

  const fetchRecentJobs = async () => {
    try {
      const response = await fetch("/api/jobs");
      if (response.ok) {
        const data = await response.json();
        setRecentJobs((data || []).slice(0, 3));
      }
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* ── Hero ──────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white overflow-hidden pt-20">
        {/* Background grid decoration */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "linear-gradient(rgba(7,200,249,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(7,200,249,0.3) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-4 text-center animate-fade-in">
          <div className="inline-flex items-center gap-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-cyan-300 text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2 rounded-full mb-6 sm:mb-8">
            <CheckCircleIcon className="w-4 h-4" />
            Quispamsis, New Brunswick's Trusted Craftspeople
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-4 sm:mb-6 leading-tight">
            Built Right.{" "}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Built to Last.
            </span>
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-2xl mx-auto mb-8 sm:mb-10">
            Expert carpentry and renovation services — from custom builds to
            full home renovations. Your vision brought to life with precision
            and care.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-16 sm:mb-20">
            <Link
              to="/contact"
              className="px-6 sm:px-10 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold text-base sm:text-lg rounded-lg hover:shadow-2xl hover:shadow-cyan-500/30 transform hover:scale-105 transition-all duration-300 w-full sm:w-auto"
            >
              Start Your Project →
            </Link>
            <Link
              to="/showcase"
              className="px-6 sm:px-10 py-3 sm:py-4 bg-white bg-opacity-10 border border-white border-opacity-30 text-white font-bold text-base sm:text-lg rounded-lg hover:bg-opacity-20 transform hover:scale-105 transition-all duration-300 w-full sm:w-auto"
            >
              View Our Work
            </Link>
          </div>
        </div>
        {/* Scroll hint - positioned to not overlap with buttons */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex justify-center pointer-events-none">
          <div className="text-gray-400 text-xs sm:text-sm animate-bounce-gentle">
            ↓ Scroll
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ─────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4 text-gray-800">
              Why Choose Us
            </h2>
            <p className="text-xl text-gray-500 max-w-xl mx-auto">
              We don't just build — we take pride in every cut, joint, and
              finish.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: SparklesIcon,
                title: "Quality Craftsmanship",
                desc: "Meticulous attention to every detail, from raw materials to the final finish.",
              },
              {
                icon: CheckCircleIcon,
                title: "Custom Solutions",
                desc: "Tailored designs that match your unique vision and functional requirements.",
              },
              {
                icon: UserGroupIcon,
                title: "Expert Team",
                desc: "Experienced craftspeople dedicated to delivering on time, every time.",
              },
              {
                icon: LightBulbIcon,
                title: "Innovation",
                desc: "Modern techniques and materials combined with timeless craftsmanship.",
              },
              {
                icon: FireIcon,
                title: "Customer First",
                desc: "Clear communication throughout — you're never left in the dark.",
              },
              {
                icon: BoltIcon,
                title: "Reliable Service",
                desc: "Honest pricing, dependable execution, and professional project management.",
              },
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div
                  key={idx}
                  className="p-8 bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
                >
                  <Icon className="w-12 h-12 text-blue-600 mb-5" />
                  <h3 className="text-xl font-bold mb-3 text-gray-800">
                    {feature.title}
                  </h3>
                  <p className="text-gray-500 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Recent Projects ───────────────────────────────────── */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4">
              Recent{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Projects
              </span>
            </h2>
            <p className="text-xl text-gray-300">
              A sample of our latest completed work
            </p>
          </div>

          {recentJobs.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {recentJobs.map((job, idx) => (
                <Link
                  key={job.id}
                  to={`/job/${job.slug}`}
                  className="group block bg-white bg-opacity-5 border border-white border-opacity-10 rounded-2xl overflow-hidden hover:bg-opacity-10 hover:border-cyan-400 hover:border-opacity-50 transition-all duration-300 hover-card animate-fade-in"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className="bg-gradient-to-br from-blue-700 to-cyan-600 h-44 flex items-center justify-center">
                    <BuildingOfficeIcon className="w-16 h-16 text-white" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2 group-hover:text-cyan-400 transition-colors duration-300">
                      {job.title}
                    </h3>
                    <p className="text-gray-200 text-sm mb-4 line-clamp-2">
                      {job.description}
                    </p>
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-gray-400">
                        ✓{" "}
                        {new Date(job.completion_date).toLocaleDateString(
                          "en-CA",
                          { year: "numeric", month: "short" },
                        )}
                      </p>
                      <span className="text-cyan-400 text-sm font-semibold group-hover:translate-x-1 transition-transform duration-300 inline-block">
                        View →
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <BuildingOfficeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">Projects coming soon…</p>
            </div>
          )}

          <div className="text-center">
            <Link
              to="/showcase"
              className="inline-block bg-white bg-opacity-10 border border-white border-opacity-30 text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-opacity-20 transform hover:scale-105 transition-all duration-300"
            >
              View Full Showcase →
            </Link>
          </div>
        </div>
      </section>

      {/* ── Process ───────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4 text-gray-800">
              Our Process
            </h2>
            <p className="text-xl text-gray-500">
              Simple steps from idea to completion
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                step: "1",
                title: "Consultation",
                desc: "Discuss your vision and goals",
                icon: ChatBubbleLeftIcon,
              },
              {
                step: "2",
                title: "Design",
                desc: "Detailed plans and proposals",
                icon: RectangleStackIcon,
              },
              {
                step: "3",
                title: "Execution",
                desc: "Expert craftsmanship in action",
                icon: WrenchIcon,
              },
              {
                step: "4",
                title: "Delivery",
                desc: "Handover of completed project",
                icon: SparklesIcon,
              },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="relative text-center">
                  <div className="bg-gradient-to-br from-blue-600 to-cyan-500 text-white rounded-2xl w-20 h-20 flex flex-col items-center justify-center mx-auto mb-5 shadow-lg">
                    <Icon className="w-8 h-8" />
                    <span className="text-xs font-bold opacity-75 mt-1">
                      Step {item.step}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-500 text-sm">{item.desc}</p>
                  {idx < 3 && (
                    <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-blue-300 to-cyan-300" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white text-center px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-5xl font-bold mb-6">
            Ready to Transform{" "}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Your Space?
            </span>
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Let's bring your vision to life with expert craftsmanship and honest
            service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-10 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold text-lg rounded-lg hover:shadow-2xl hover:shadow-cyan-500/30 transform hover:scale-105 transition-all duration-300"
            >
              Contact Us →
            </Link>
            <Link
              to="/showcase"
              className="px-10 py-4 bg-white bg-opacity-10 border border-white border-opacity-30 text-white font-bold text-lg rounded-lg hover:bg-opacity-20 transform hover:scale-105 transition-all duration-300"
            >
              View Showcase
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
